import { Box, Button, Dialog, DialogContent, IconButton, LinearProgress, TextField, Typography } from '@mui/material'
import CloseIcon from '@material-ui/icons/Close'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import axios from 'axios'
import Locale from 'date-fns/locale/en-GB'
import moment from 'moment'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import RecruitmentCandidates from './RecruitmentCandidates'
import { toast } from 'react-toastify'
import StaffingHistory from '../Staffing_Request_History/Staffing_Request_History'

const STATUS_LABELS = {
  pending_workforce: { label: 'Në pritje (Workforce)', color: '#5d55c5', background: '#f0efff', icon: '⌛' },
  approved: { label: 'Aktive', color: '#2563eb', background: '#edf4ff', icon: '↗' },
  rejected: { label: 'Refuzuar', color: '#d23c4a', background: '#ffedef', icon: '×' },
  completed: { label: 'Përfunduar', color: '#16835e', background: '#e9f9f2', icon: '✓' },
  overdue: { label: 'Afati ka skaduar', color: '#bd5b1b', background: '#fff2e8', icon: '!' }
}

const StaffingRequestDetails = ({ open, onClose, item, hasAccessToRecruit, hasWorkforceAccess, hasAccessToApprove = hasWorkforceAccess, hasAccessToEdit, hasAccessToEditRequested = hasAccessToEdit, canCompleteRequest, refreshAfterAction }) => {
  const [showEdit, setShowEdit] = useState('')
  const [inputValue, setInputValue] = useState(1)
  const [editRequest, setEditRequest] = useState(false)
  const [candidates, setCandidates] = useState([])
  const previousCandidates = (item.recruitment_history || []).flatMap((entry) => entry.candidates || [])
  const candidatesPayload = candidates.map(({ applicant_id, name }) => (applicant_id ? { applicant_id } : { name }))
  const [comment, setComment] = useState('')
  const [commentError, setCommentError] = useState(false)
  const [showRejectForm, setShowRejectForm] = useState(false)
  const [rejectComment, setRejectComment] = useState('')
  const [rejectError, setRejectError] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [showCompleteForm, setShowCompleteForm] = useState(false)
  const [completeComment, setCompleteComment] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showEditDueDate, setShowEditDueDate] = useState(false)
  const [dueDateValue, setDueDateValue] = useState(null)

  const status = item.status || 'pending_workforce'
  const isPending = status === 'pending_workforce'
  const isApproved = status === 'approved'
  const isRejected = status === 'rejected'
  const isCompleted = status === 'completed'
  const isOverdue = status === 'overdue'
  const isClosed = isCompleted || isRejected
  const completeButton = canCompleteRequest && !isClosed && !showCompleteForm ? (
    <Button variant='outlined' size='small' disabled={actionLoading} onClick={() => setShowCompleteForm(true)}>
      Përfundo kërkesën
    </Button>
  ) : null
  const requiredCandidates = isOverdue ? Math.max(0, Number(inputValue) - item.recruited) : Number(inputValue)
  const candidateCountMismatch = !Number.isInteger(requiredCandidates) || candidates.length !== requiredCandidates

  const onNumberInputWheel = (e) => {
    try {
      if (e && e.target && e.target.blur) e.target.blur()
    } catch (err) {}
  }

  const onNumberInputFocus = (e) => {
    try {
      const el = e && e.target
      if (el && el.addEventListener) el.addEventListener('wheel', onNumberInputWheel, { passive: true })
    } catch (err) {}
  }

  const onNumberInputBlur = (e) => {
    try {
      const el = e && e.target
      if (el && el.removeEventListener) el.removeEventListener('wheel', onNumberInputWheel)
    } catch (err) {}
  }

  const updateStaffingRequest = async () => {
    if (!editRequest && candidateCountMismatch) {
      toast.error('Numri i kandidatëve duhet të përputhet me numrin e të rekrutuarve.')
      return
    }
    let postData = {}
    if (!comment.trim()) {
      setCommentError(true)
      return
    }
    if (editRequest) {
      postData = {
        requested: inputValue - item.requested,
        comment: comment,
        type: 'requested'
      }
    } else {
      postData = {
        recruited: +inputValue,
        type: 'recruited',
        comment: comment.trim(),
        candidates: candidatesPayload
      }
    }
    axios
      .patch(`staffingRequest/${showEdit}`, postData)
      .then((res) => {
        if (res.data.success) {
          toast.success('Kërkesa u përditësua me sukses!')
          refreshAfterAction(isPending ? 'edit_pending' : 'recruit')
          setShowEdit('')
          setInputValue(1)
          setEditRequest(false)
          setComment('')
          setCandidates([])
        } else {
          toast.error(res.data.message)
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || 'Ndodhi një gabim!')
        console.log('error', err)
      })
  }

  const completeRequest = async () => {
    if (!completeComment.trim()) return
    setActionLoading(true)
    try {
      const res = await axios.post(`staffingRequest/${item._id}/close`, { comment: completeComment.trim() })
      if (res.data.success) {
        toast.success('Kërkesa u përfundua!')
        setShowCompleteForm(false)
        setCompleteComment('')
        await refreshAfterAction('complete')
        onClose()
      } else {
        toast.error(res.data.message)
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Ndodhi një gabim gjatë përfundimit!')
    } finally {
      setActionLoading(false)
    }
  }

  const approveRequest = async () => {
    setActionLoading(true)
    try {
      const res = await axios.post(`staffingRequest/${item._id}/approve`)
      if (res.data.success) {
        toast.success('Kërkesa u aprovua!')
        refreshAfterAction('approve')
      } else {
        toast.error(res.data.message)
      }
    } catch (err) {
      toast.error('Ndodhi një gabim gjatë aprovimit!')
      console.log('error', err)
    } finally {
      setActionLoading(false)
    }
  }

  const rejectRequest = async () => {
    if (!rejectComment.trim()) {
      setRejectError(true)
      return
    }
    setActionLoading(true)
    try {
      const res = await axios.post(`staffingRequest/${item._id}/reject`, { comment: rejectComment.trim() })
      if (res.data.success) {
        toast.success('Kërkesa u refuzua!')
        refreshAfterAction('reject')
        setShowRejectForm(false)
        setRejectComment('')
        setRejectError(false)
      } else {
        toast.error(res.data.message)
      }
    } catch (err) {
      toast.error('Ndodhi një gabim gjatë refuzimit!')
      console.log('error', err)
    } finally {
      setActionLoading(false)
    }
  }

  const deleteRequest = async () => {
    setActionLoading(true)
    try {
      const res = await axios.delete(`staffingRequest/${item._id}`)
      if (res.data.success) {
        toast.success('Kërkesa u fshi me sukses!')
        refreshAfterAction('delete')
      } else {
        toast.error(res.data.message)
      }
    } catch (err) {
      toast.error('Ndodhi një gabim gjatë fshirjes!')
      console.log('error', err)
    } finally {
      setActionLoading(false)
      setShowDeleteConfirm(false)
    }
  }

  const updateDueDate = async () => {
    if (candidateCountMismatch) {
      toast.error('Numri i kandidatëve duhet të përputhet me numrin e personave që po shtohen në rekrutim.')
      return
    }
    if (!dueDateValue) {
      toast.error('Data e fundit është e nevojshme!')
      return
    }

    if (!comment.trim()) {
      setCommentError(true)
      return
    }

    setActionLoading(true)
    try {
      const res = await axios.patch(`staffingRequest/${item._id}`, {
        due_date: dueDateValue,
        recruited_total: +inputValue,
        comment: comment.trim(),
        type: 'overdue',
        candidates: candidatesPayload
      })
      if (res.data.success) {
        toast.success('Kërkesa u përditësua me sukses!')
        refreshAfterAction('edit_overdue')
        setShowEditDueDate(false)
        setDueDateValue(null)
        setInputValue(1)
        setComment('')
        setCandidates([])
        setCommentError(false)
      } else {
        toast.error(res.data.message)
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Ndodhi një gabim!')
      console.log('error', err)
    } finally {
      setActionLoading(false)
    }
  }

  const positionName = typeof item.position === 'object' ? item.position?.name : item.position
  const requestedPositions = Number(item.requested)
  const recruitedPositions = Number(item.recruited)
  const requestedCount = Number.isFinite(requestedPositions) ? requestedPositions : 0
  const recruitedCount = Number.isFinite(recruitedPositions) ? recruitedPositions : 0

  const detailsArray = [
    { title: 'Pozicioni:', value: positionName },
    { title: 'Gjuha:', value: item.language?.name },
    { title: 'Departamenti:', value: item.department?.name },
    { title: 'Lokacioni:', value: item?.location },
    { title: 'Lloji kerkeses:', value: item?.request_type },
    { title: 'Të kërkuar:', value: requestedCount },
    { title: 'Të rekrutuar:', value: recruitedCount },
    { title: 'Të hapura:', value: Math.max(requestedCount - recruitedCount, 0) },
    { title: 'Data fundit:', value: moment(item.due_date).format('DD/MM/YYYY') },
    { title: 'Data kerkeses:', value: moment(item.created_at).format('DD/MM/YYYY') },
    { title: 'Krijuar nga:', value: `${item.created_by?.first_name} ${item.created_by?.last_name}` },
    { title: 'Komenti:', value: item?.creationComment }
  ]

  const historyRequested = Array.isArray(item?.requested_history)
    ? item.requested_history.map((h) => [
        { title: 'Ndryshuar:', value: `Nga  ${h.requestedBefore} në ${h.requestedAfter}` },
        { title: 'Ndryshuar më:', value: moment(h.date).format('DD/MM/YYYY HH:mm') },
        { title: 'Ndryshuar nga:', value: `${h.recruiter?.first_name} ${h.recruiter?.last_name}` },
        { title: 'Komenti:', value: h.comment }
      ])
    : []

  const histortRecruited = Array.isArray(item?.recruitment_history)
    ? item.recruitment_history.map((h) => [
        { title: 'Ndryshuar:', value: `Nga  ${h.recruitedBefore} në ${h.recruitedAfter}` },
        { title: 'Ndryshuar më:', value: moment(h.date).format('DD/MM/YYYY HH:mm') },
        { title: 'Ndryshuar nga:', value: `${h.recruiter?.first_name} ${h.recruiter?.last_name}` },
        ...(h.candidates?.length
          ? [
              {
                title: 'Kandidatët:',
                value: (
                  <Box component='span' sx={{ display: 'block' }}>
                    {h.candidates.map((candidate, index) => (
                      <Box key={candidate.applicant_id || index} sx={{ overflowWrap: 'anywhere' }}>
                        {candidate.applicant_id && hasAccessToRecruit ? (
                          <Link to={`/applicants/${candidate.applicant_id}`}>{candidate.name}</Link>
                        ) : (
                          candidate.name
                        )}
                        {!candidate.applicant_id && ' (manual)'}
                      </Box>
                    ))}
                  </Box>
                )
              }
            ]
          : []),
        ...(h.comment
          ? [{ title: 'Komenti:', value: <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{h.comment}</span> }]
          : [])
      ])
    : []

  const minInputValue = editRequest ? (isPending ? 0 : item.recruited) : 1
  const maxInputValue = editRequest ? Infinity : item.requested - item.recruited

  const updateInputValue = (newValue) => {
    const n = Number(newValue)
    if (n >= minInputValue && n <= maxInputValue) {
      setInputValue(n)
    }
  }

  const statusMeta = STATUS_LABELS[status] || STATUS_LABELS.pending_workforce
  const workforceActor = item.workforce_action_by ? `${item.workforce_action_by.first_name} ${item.workforce_action_by.last_name}` : null
  const workforceActionDate = item.workforce_action_at ? moment(item.workforce_action_at).format('DD/MM/YYYY HH:mm') : null
  const progress = requestedCount > 0 ? Math.min(100, Math.round((recruitedCount / requestedCount) * 100)) : 0
  const daysLeft = moment(item.due_date).startOf('day').diff(moment().startOf('day'), 'days')
  const detailTitles = ['Gjuha:', 'Lloji kerkeses:', 'Lokacioni:', 'Departamenti:', 'Data kerkeses:', 'Data fundit:', 'Krijuar nga:']
  return (
    <Dialog
      open={open}
      onClose={actionLoading ? undefined : onClose}
      fullWidth
      maxWidth='sm'
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 24px)', sm: 660 },
          maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 56px)' },
          m: { xs: 1.5, sm: 3.5 },
          borderRadius: '16px',
          overflow: 'hidden'
        }}
      }
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: 2, borderBottom: '1px solid #eaecf0' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Box
                component='span'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  px: 1,
                  py: 0.45,
                  borderRadius: '999px',
                  color: statusMeta.color,
                  backgroundColor: statusMeta.background,
                  fontSize: 11,
                  lineHeight: 1.2,
                  fontWeight: 700
                }}
              >
                <span aria-hidden='true'>{statusMeta.icon}</span>
                {statusMeta.label}
              </Box>
              <Typography sx={{ mt: 1.25, color: '#101828', fontSize: { xs: 19, sm: 22 }, lineHeight: 1.25, fontWeight: 750 }}>
                {positionName || '—'}
              </Typography>
              <Typography sx={{ mt: 0.5, color: '#667085', fontSize: 12.5 }}>
                {[item.department?.name, item.location, item.language?.name].filter(Boolean).join(' · ') || '—'}
              </Typography>
            </Box>
            <IconButton aria-label='Mbyll detajet' onClick={onClose} disabled={actionLoading} sx={{ backgroundColor: '#f2f4f7', borderRadius: '9px' }}>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: '1px solid #eaecf0' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.25 }}>
            {[
              { label: 'TË KËRKUAR', value: requestedCount },
              { label: 'REKRUTUAR', value: recruitedCount },
              { label: 'TË HAPURA', value: Math.max(requestedCount - recruitedCount, 0), alert: true }
            ].map((stat) => (
              <Box key={stat.label} sx={{ p: 1.5, border: '1px solid', borderColor: stat.alert ? '#ffd7d9' : '#e7ebf1', borderRadius: '10px', backgroundColor: stat.alert ? '#fff1f2' : '#f8fafc' }}>
                <Typography sx={{ color: stat.alert ? '#d92d20' : '#98a2b3', fontSize: 10.5, fontWeight: 700 }}>{stat.label}</Typography>
                <Typography sx={{ color: stat.alert ? '#d92d20' : '#101828', fontSize: 20, fontWeight: 750 }}>{stat.value}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.6, mb: 0.65 }}>
            <Typography sx={{ color: '#667085', fontSize: 11.5 }}>Ecuria e rekrutimit</Typography>
            <Typography sx={{ color: '#101828', fontSize: 11.5, fontWeight: 700 }}>{progress}%</Typography>
          </Box>
          <LinearProgress variant='determinate' value={progress} sx={{ height: 6, borderRadius: 6, backgroundColor: '#e7ecf4', '& .MuiLinearProgress-bar': { backgroundColor: '#356ae6', borderRadius: 6 } }} />
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: '1px solid #eaecf0' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 4, rowGap: 1.5 }}>
            {detailsArray.filter((detail) => detailTitles.includes(detail.title)).map((detail) => (
              <Box key={detail.title} sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#98a2b3', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase' }}>
                  {detail.title.replace(':', '')}
                </Typography>
                <Typography sx={{ mt: 0.25, color: '#344054', fontSize: 12.5, overflowWrap: 'anywhere' }}>
                  {detail.title === 'Data fundit:' ? `${detail.value}${!isClosed ? ` · ${daysLeft} ditë` : ''}` : (detail.value || '—')}
                </Typography>
              </Box>
            ))}
            <Box>
              <Typography sx={{ color: '#98a2b3', fontSize: 10.5, fontWeight: 700 }}>STATUSI</Typography>
              <Typography sx={{ mt: 0.25, color: '#344054', fontSize: 12.5 }}>{statusMeta.label}</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: '1px solid #eaecf0' }}>
          <Typography sx={{ color: '#98a2b3', fontSize: 10.5, fontWeight: 700, mb: 0.75 }}>KOMENTI</Typography>
          <Box sx={{ p: 1.5, minHeight: 42, backgroundColor: '#f8fafc', border: '1px solid #eaecf0', borderRadius: '9px', color: '#475467', fontSize: 12.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {item.creationComment || '—'}
          </Box>
        </Box>

        {(isApproved || isRejected || isCompleted) && workforceActor && (
          <Box sx={{ mx: { xs: 2, sm: 3 }, mt: 2, p: 1.5, backgroundColor: '#f8fafc', borderRadius: 1 }}>
            <Typography variant='caption' sx={{ display: 'block', fontWeight: 'bold' }}>
              {isRejected ? 'Refuzuar nga:' : 'Aprovuar nga:'}
            </Typography>
            <Typography variant='body2'>
              {workforceActor} {workforceActionDate ? `— ${workforceActionDate}` : ''}
            </Typography>
            {isRejected && item.rejection_comment && (
              <>
                <Typography variant='caption' sx={{ display: 'block', fontWeight: 'bold', mt: 0.5 }}>
                  Arsyeja:
                </Typography>
                <Typography variant='body2'>{item.rejection_comment}</Typography>
              </>
            )}
          </Box>
        )}

        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        {isPending && (hasWorkforceAccess || hasAccessToApprove) && showEdit !== item._id && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', marginTop: '10px' }}>
            {!showRejectForm ? (
              <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {hasAccessToApprove && <Button variant='contained' color='success' size='small' onClick={approveRequest} disabled={actionLoading}>
                  Aprovo
                </Button>}
                {hasWorkforceAccess && <>
                <Button variant='outlined' color='error' size='small' onClick={() => setShowRejectForm(true)} disabled={actionLoading}>
                  Refuzo
                </Button>
                <Button
                  variant='outlined'
                  size='small'
                  onClick={() => {
                    setShowEdit(item._id)
                    setInputValue(item.requested)
                    setEditRequest(true)
                  }}
                  disabled={actionLoading}
                >
                  Ndrysho
                </Button>
                </>}
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <TextField
                  multiline
                  minRows={2}
                  fullWidth
                  value={rejectComment}
                  onChange={(e) => {
                    setRejectError(e.target.value.trim().length === 0)
                    setRejectComment(e.target.value)
                  }}
                  placeholder='Arsyeja e refuzimit'
                  error={rejectError}
                  helperText={rejectError ? 'Arsyeja është e nevojshme' : ''}
                  sx={{ marginBottom: '10px', fieldset: { border: '1px solid' } }}
                />
                <Box sx={{ display: 'flex', gap: '5px' }}>
                  <Button
                    size='small'
                    variant='contained'
                    color='error'
                    onClick={rejectRequest}
                    disabled={actionLoading || rejectError || !rejectComment.trim()}
                  >
                    Konfirmo refuzimin
                  </Button>
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() => {
                      setShowRejectForm(false)
                      setRejectComment('')
                      setRejectError(false)
                    }}
                    disabled={actionLoading}
                  >
                    Mbrapa
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {isPending && hasAccessToEditRequested && !hasWorkforceAccess && showEdit !== item._id && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', width: '100%', marginTop: '10px' }}>
            <Button
              variant='outlined'
              size='small'
              onClick={() => {
                setShowEdit(item._id)
                setInputValue(item.requested)
                setEditRequest(true)
              }}
            >
              Ndrysho
            </Button>
            {completeButton}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', width: '100%' }}>
          {((isApproved && !isClosed && (hasAccessToRecruit || hasAccessToEditRequested)) ||
            (isPending && (hasWorkforceAccess || hasAccessToEditRequested) && showEdit === item._id)) &&
            (showEdit === '' || showEdit !== item._id ? (
              <>
                {(hasAccessToRecruit || hasAccessToEdit) && <Button
                  variant='outlined'
                  sx={{ alignSelf: 'self-end', marginTop: '10px' }}
                  size='small'
                  onClick={() => {
                    setShowEdit(item._id)
                    setInputValue(1)
                  }}
                >
                  Rekruto
                </Button>}
                <Button
                  variant='outlined'
                  sx={{ alignSelf: 'self-end', marginTop: '10px' }}
                  size='small'
                  onClick={() => {
                    setShowEdit(item._id)
                    setInputValue(item.requested)
                    setEditRequest(true)
                  }}
                >
                  Ndrysho
                </Button>
                {completeButton && <Box sx={{ mt: '10px' }}>{completeButton}</Box>}
              </>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  mt: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { border: '1px solid #9ca3af' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main', borderWidth: 2 },
                  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' }
                }}
              >
                <TextField
                  label={editRequest ? 'Numri i kërkuar' : 'Shto apo zbrit të rekrutuar'}
                  size='small'
                  variant='outlined'
                  type='number'
                  value={inputValue}
                  fullWidth
                  onChange={(e) => updateInputValue(e.target.value)}
                  onFocus={onNumberInputFocus}
                  onBlur={onNumberInputBlur}
                  InputProps={{
                    inputProps: {
                      min: minInputValue,
                      ...(editRequest ? {} : { max: maxInputValue })
                    },
                    endAdornment: (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        {inputValue > minInputValue && (
                          <>
                            <Button
                              size='small'
                              onClick={() => updateInputValue(Number(inputValue) - 1)}
                              sx={{
                                minWidth: '30px',
                                height: '32px',
                                padding: 0,
                                borderRadius: 0,
                                backgroundColor: 'transparent',
                                color: '#000',
                                fontSize: '14px',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                              }}
                            >
                              -
                            </Button>
                            <Box
                              sx={{
                                width: '1px',
                                height: '20px',
                                backgroundColor: 'rgba(0,0,0,0.23)'
                              }}
                            />
                          </>
                        )}
                        <Button
                          size='small'
                          onClick={() => updateInputValue(Number(inputValue) + 1)}
                          sx={{
                            minWidth: '30px',
                            height: '32px',
                            padding: 0,
                            borderRadius: 0,
                            backgroundColor: 'transparent',
                            color: '#000',
                            fontSize: '14px',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                          }}
                        >
                          +
                        </Button>
                      </Box>
                    )
                  }}
                />
                {!editRequest && (
                  <RecruitmentCandidates
                    requiredCount={requiredCandidates}
                    previousCandidates={previousCandidates}
                    candidates={candidates}
                    onChange={setCandidates}
                    canSelectApplicants={hasAccessToRecruit}
                  />
                )}
                <TextField
                  label='Komenti'
                  multiline={true}
                  minRows={3}
                  fullWidth
                  value={comment}
                  onChange={(e) => {
                    setCommentError(e.target.value.length === 0)
                    setComment(e.target.value)
                  }}
                  sx={{
                    boxSizing: 'border-box'
                  }}
                  placeholder='Komenti'
                  inputProps={{ maxLength: 5000 }}
                  error={commentError}
                  helperText={commentError ? 'Komenti i nevojshëm' : ''}
                  FormHelperTextProps={{ sx: { textAlign: 'left', whiteSpace: 'nowrap', fontSize: '11px' } }}
                />

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    variant='contained'
                    onClick={updateStaffingRequest}
                    disabled={
                      (editRequest ? inputValue === item.requested : inputValue === 0 || inputValue > maxInputValue) ||
                      commentError ||
                      (!editRequest && candidateCountMismatch)
                    }
                  >
                    Ruaj
                  </Button>
                  <Button
                    size='small'
                    variant='contained'
                    onClick={() => {
                      setShowEdit('')
                      setEditRequest(false)
                      setComment('')
                      setCandidates([])
                      setCommentError(false)
                    }}
                    color='error'
                  >
                    Mbrapa
                  </Button>
                </Box>
              </Box>
            ))}
        </Box>
        {isOverdue && (hasWorkforceAccess || hasAccessToEdit) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', marginTop: '10px' }}>
            {!showEditDueDate ? (
              <Button
                variant='outlined'
                size='small'
                onClick={() => {
                  setShowEditDueDate(true)
                  setDueDateValue(new Date(item.due_date))
                  setInputValue(item.recruited)
                }}
                disabled={actionLoading}
              >
                Ndrysho
              </Button>
            ) : (
              <Box sx={{ width: '100%' }}>
                <Box sx={{ mb: 3 }}>
                  <TextField
                    label='Numri i rekrutuar'
                    size='small'
                    variant='outlined'
                    type='number'
                    value={inputValue}
                    fullWidth
                    onChange={(e) => {
                      const value = Number(e.target.value)
                      if (value >= 0 && value <= item.requested) setInputValue(value)
                    }}
                    inputProps={{ min: 0, max: item.requested }}
                    sx={{ fieldset: { border: '1px solid' } }}
                    onFocus={onNumberInputFocus}
                    onBlur={onNumberInputBlur}
                  />
                </Box>
                <RecruitmentCandidates
                  requiredCount={requiredCandidates}
                  previousCandidates={previousCandidates}
                  candidates={candidates}
                  onChange={setCandidates}
                  canSelectApplicants={hasAccessToRecruit}
                  disabled={actionLoading}
                />
                <Box sx={{ mb: 3 }}>
                  <TextField
                    multiline
                    minRows={2}
                    fullWidth
                    value={comment}
                    onChange={(e) => {
                      setComment(e.target.value)
                      setCommentError(!e.target.value.trim())
                    }}
                    placeholder='Komenti'
                    error={commentError}
                    helperText={commentError ? 'Komenti i nevojshëm' : ''}
                    sx={{ fieldset: { border: '1px solid' } }}
                  />
                </Box>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={Locale}>
                  <DatePicker
                    label='Data fundit'
                    value={dueDateValue}
                    onChange={(value) => setDueDateValue(value)}
                    minDate={new Date()}
                    inputFormat='dd/MM/yyyy'
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size='small'
                        fullWidth
                        inputProps={{ ...params.inputProps, readOnly: true }}
                        sx={{ marginBottom: '10px', fieldset: { border: '1px solid' } }}
                      />
                    )}
                  />
                </LocalizationProvider>
                <Box sx={{ display: 'flex', gap: '10px', mt: 2 }}>
                  <Button
                    size='small'
                    variant='contained'
                    onClick={updateDueDate}
                    disabled={actionLoading || !dueDateValue || candidateCountMismatch}
                  >
                    Ruaj
                  </Button>
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() => {
                      setShowEditDueDate(false)
                      setDueDateValue(null)
                      setInputValue(1)
                      setComment('')
                      setCandidates([])
                      setCommentError(false)
                    }}
                    disabled={actionLoading}
                  >
                    Mbrapa
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
        {isOverdue && hasWorkforceAccess && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', marginTop: '10px' }}>
            {!showDeleteConfirm ? (
              <Button
                variant='outlined'
                color='error'
                size='small'
                onClick={() => setShowDeleteConfirm(true)}
                disabled={actionLoading || showEditDueDate}
              >
                Fshi
              </Button>
            ) : (
              <Box>
                <Typography variant='body2' sx={{ mb: 1 }}>
                  Jeni i sigurt që dëshironi ta fshini këtë kërkesë?
                </Typography>
                <Box sx={{ display: 'flex', gap: '5px' }}>
                  <Button size='small' variant='contained' color='error' onClick={deleteRequest} disabled={actionLoading}>
                    Konfirmo fshirjen
                  </Button>
                  <Button size='small' variant='outlined' onClick={() => setShowDeleteConfirm(false)} disabled={actionLoading}>
                    Mbrapa
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {isOverdue && completeButton && <Box sx={{ mt: 2 }}>{completeButton}</Box>}
        {canCompleteRequest && !isClosed && showCompleteForm && (
          <Box sx={{ mt: 2, width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant='body2'>
                  Kërkesa do të shënohet si e përfunduar. Numri i të kërkuarve dhe të rekrutuarve nuk ndryshon.
                </Typography>
                <TextField
                  label='Arsyeja e përfundimit'
                  variant='outlined'
                  fullWidth
                  minRows={3}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #9ca3af' },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667085' },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main', borderWidth: 2 }
                  }}
                  required
                  multiline
                  value={completeComment}
                  onChange={(event) => setCompleteComment(event.target.value)}
                  inputProps={{ maxLength: 5000 }}
                  disabled={actionLoading}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant='contained' onClick={completeRequest} disabled={actionLoading || !completeComment.trim()}>
                    Konfirmo përfundimin
                  </Button>
                  <Button color='error' disabled={actionLoading} onClick={() => { setShowCompleteForm(false); setCompleteComment('') }}>
                    Anulo
                  </Button>
                </Box>
              </Box>
          </Box>
        )}
        {!!item.closure_history?.length && (
          <Box sx={{ mt: 2 }}>
            <StaffingHistory title='Historiku i përfundimit' history={item.closure_history.map((entry) => [
              { title: 'Përfunduar nga:', value: `${entry.actor?.first_name || ''} ${entry.actor?.last_name || ''}` },
              { title: 'Përfunduar më:', value: moment(entry.date).format('DD/MM/YYYY HH:mm') },
              { title: 'Statusi i mëparshëm:', value: STATUS_LABELS[entry.previous_status]?.label || entry.previous_status },
              { title: 'Të kërkuar / të rekrutuar:', value: `${entry.requested} / ${entry.recruited}` },
              { title: 'Komenti:', value: entry.comment }
            ])} />
          </Box>
        )}

        <Box sx={{ marginTop: '20px' }}>
          <StaffingHistory history={histortRecruited} title={'Historiku i rekrutimit'} />
          <StaffingHistory history={historyRequested} title={'Historiku i kërkesës'} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 1.5, borderTop: '1px solid #eaecf0' }}>
          <Button variant='outlined' onClick={onClose} disabled={actionLoading} sx={{ textTransform: 'none', borderColor: '#d0d5dd', color: '#344054' }}>
            Mbyll
          </Button>
        </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default StaffingRequestDetails
